import { useCallback, useEffect, useState } from 'react'
import { Loader2, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchProducts } from '@/lib/api/products'
import { fetchServiceConsumables, updateServiceConsumables } from '@/lib/api/serviceConsumables'
import { cn } from '@/lib/utils'

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

/**
 * @param {object} props
 * @param {number | null} props.serviceId
 * @param {boolean} [props.disabled]
 */
export function ServiceConsumablesPanel({ serviceId, disabled = false }) {
  const [lines, setLines] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const load = useCallback(async () => {
    if (!serviceId) {
      setLines([])
      return
    }

    setLoading(true)
    setError('')
    try {
      const [consumables, productList] = await Promise.all([
        fetchServiceConsumables(serviceId),
        fetchProducts(),
      ])
      setProducts(productList.filter((item) => item.is_active))
      setLines(
        consumables.map((row) => ({
          product_id: row.product_id,
          quantity: Number(row.quantity),
        })),
      )
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to load consumables.')
      setLines([])
    } finally {
      setLoading(false)
    }
  }, [serviceId])

  useEffect(() => {
    load()
  }, [load])

  function addLine() {
    setLines((current) => [...current, { product_id: '', quantity: 1 }])
  }

  function updateLine(index, field, value) {
    setLines((current) => current.map((line, i) => (i === index ? { ...line, [field]: value } : line)))
  }

  function removeLine(index) {
    setLines((current) => current.filter((_, i) => i !== index))
  }

  async function handleSave() {
    if (!serviceId) return

    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const payload = lines
        .filter((line) => line.product_id)
        .map((line) => ({
          product_id: Number(line.product_id),
          quantity: Number(line.quantity),
        }))

      await updateServiceConsumables(serviceId, payload)
      setSuccess('Consumables saved.')
      await load()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to save consumables.')
    } finally {
      setSaving(false)
    }
  }

  if (!serviceId) {
    return (
      <p className="text-sm text-muted-foreground">
        Select a service to edit consumables (BOM).
      </p>
    )
  }

  return (
    <div className="space-y-4 rounded-2xl border border-border/60 bg-muted/20 p-4">
      <div>
        <h3 className="text-sm font-semibold">Consumables (BOM)</h3>
        <p className="text-xs text-muted-foreground">
          Products auto-deducted from stock when this service is sold on POS.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="space-y-3">
          {lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">No consumables linked yet.</p>
          ) : null}
          {lines.map((line, index) => (
            <div key={index} className="grid gap-2 md:grid-cols-[1fr_120px_40px]">
              <select
                value={line.product_id}
                onChange={(e) => updateLine(index, 'product_id', e.target.value)}
                className={inputClass}
                disabled={disabled || saving}
              >
                <option value="">Select product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min="0.001"
                step="0.001"
                value={line.quantity}
                onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                className={inputClass}
                disabled={disabled || saving}
                placeholder="Qty"
              />
              <button
                type="button"
                onClick={() => removeLine(index)}
                className="flex h-10 items-center justify-center rounded-xl border border-border/80 hover:bg-muted"
                disabled={disabled || saving}
                aria-label="Remove line"
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={addLine} disabled={disabled || saving}>
          <Plus className="mr-1 h-4 w-4" />
          Add product
        </Button>
        <Button type="button" size="sm" onClick={handleSave} disabled={disabled || saving || loading}>
          {saving ? 'Saving…' : 'Save consumables'}
        </Button>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {success ? <p className="text-sm text-emerald-600">{success}</p> : null}
    </div>
  )
}
